import { Component } from '@theme/component';
import { DialogComponent } from '@theme/dialog';

const cssRules = `
/* Form padding */
form {
  padding: 5px 2px !important;
}

/* Input label adjustments */
label {
  top: 16px !important;
}

/* ===== DROPDOWN FLOATING LABEL PATTERN ===== */
/* Using stable selectors based on aria-controls attribute */
/* Only apply to custom dropdowns (Message Type, Customer Type) */
/* Excludes phone country selector and other system dropdowns */

/* Dropdown container - identified by marker attribute */
div[data-lindy-dropdown] {
  position: relative !important;
}

/* Dropdown label - absolute positioned like input label */
div[data-lindy-dropdown] > p:first-child {
  pointer-events: none !important;
  position: absolute !important;
  left: 15px !important;
  top: 16px !important;
  z-index: 2 !important;
  font-size: 14px !important;
  line-height: 1 !important;
  color: var(--form-placeholder-color) !important;
  margin: 0 !important;
  padding: 0 !important;
  transform-origin: top left !important;
  transition: all 0.2s cubic-bezier(0, 0, 0.2, 1) !important;
}

/* Dropdown button - styled like input */
div[data-lindy-dropdown] > button[type="button"] {
  width: 100%;
  padding: 22.5px 15px 7.5px 15px !important;
  min-height: 48px;
  background-color: var(--form-background-color) !important;
  box-shadow: var(--form-field-border) !important;
  border: none !important;
  border-radius: 5px !important;
  display: flex !important;
  align-items: center !important;
  cursor: pointer !important;
  transition: box-shadow 0.2s ease !important;
  text-align: left !important;
}

/* Dropdown error state */
div[data-lindy-dropdown]:has(> output[role="alert"]) > button[type="button"],
div[data-lindy-dropdown] > button[type="button"][class*="Error"] {
  box-shadow: 0 0 0 2px var(--form-error-color) !important;
}

/* Dropdown focus state */
div[data-lindy-dropdown] > button[type="button"]:focus-within,
div[data-lindy-dropdown]:has(> output[role="alert"]) > button[type="button"]:focus-within,
div[data-lindy-dropdown] > button[type="button"][class*="Error"]:focus-within {
  box-shadow: var(--form-field-border-focused) !important;
  outline: none !important;
}


/* Toggle text - first span inside button */
div[data-lindy-dropdown] > button[type="button"] > span:first-child {
  flex: 1;
  text-align: left !important;
  font-size: 14px !important;
  color: var(--form-text-color) !important;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Arrow icon - last span inside button */
div[data-lindy-dropdown] > button[type="button"] > span:nth-child(2) {
  margin-top: 0 !important;
  margin-left: 8px !important;
  flex-shrink: 0;
  display: flex !important;
  align-items: center !important;
}

/* Label floats up when dropdown has value */
div[data-lindy-dropdown][data-has-value] > p:first-child {
  top: 6px !important;
  font-size: 11px !important;
  color: var(--form-text-color) !important;
}

/* Label floats up on focus (dropdown open) */
div[data-lindy-dropdown]:focus-within > p:first-child {
  top: 6px !important;
  font-size: 11px !important;
}
`;

/**
 * Quote Form Dialog Component
 * Manages the dialog for asking questions and email notifications about products
 *
 * @extends DialogComponent
 */
class LindyQuoteFormDialog extends DialogComponent {
  requiredRefs = ['dialog', 'modalTitle'];

  /**
   * Opens the modal and populates it with product information
   * @param {Object} productData - Product information
   * @param {string} productData.title - Product title
   * @param {string} productData.sku - Product SKU
   * @param {string} productData.url - Product URL
   * @param {string} productData.source - Source type ('Ask a Question' or 'Email Me')
   */
  openWithProductData(productData) {
    // Update modal title
    if (this.refs.modalTitle) {
      this.refs.modalTitle.textContent = productData.source;
    }

    // Show the dialog
    this.showDialog();

    // Wait for dialog to open and form to be available, then populate
    requestAnimationFrame(() => {
      this.#populateFormFields(productData);
      this.#injectShadowStyles();
    });
  }

  /**
   * Populates form fields with product data
   * @private
   */
  #populateFormFields(productData) {
    const { title, sku, url, source } = productData;

    // Format comment field content
    const commentText = `Product: ${title} | SKU: ${sku} | URL: ${url}`;

    // Find and populate Comment field
    const commentField = this.#findCommentField();
    if (commentField) {
      commentField.value = commentText;
      commentField.dispatchEvent(new Event('input', { bubbles: true }));
    }

    // Find and populate Source field
    const sourceField = this.#findSourceField();
    if (sourceField) {
      sourceField.value = source;
      sourceField.dispatchEvent(new Event('input', { bubbles: true }));
    }

    // Hide the Source field (must be done in shadow DOM)
    this.#hideSourceField();
  }

  /**
   * Recursively searches for an element including shadow DOMs
   * @private
   * @param {Element} root - Root element to search from
   * @param {string} selector - CSS selector
   * @returns {Element | null}
   */
  #querySelectorDeep(root, selector) {
    // First try regular querySelector
    let element = root.querySelector(selector);
    if (element) {
      return element;
    }

    // Search in shadow DOMs
    const allElements = root.querySelectorAll('*');
    for (const el of allElements) {
      if (el.shadowRoot) {
        element = this.#querySelectorDeep(el.shadowRoot, selector);
        if (element) {
          return element;
        }
      }
    }

    return null;
  }

  /**
   * Finds the comment/message field in the form
   * @private
   * @returns {HTMLTextAreaElement | HTMLInputElement | null}
   */
  #findCommentField() {
    const { dialog } = this.refs;

    // Try multiple selectors to find the comment field
    const selectors = [
      'textarea[name*="comment" i]',
      'textarea[name*="message" i]',
      'textarea[name*="body" i]',
      'textarea[id*="comment" i]',
      'textarea[id*="message" i]',
      'textarea',  // Fallback to any textarea
    ];

    for (const selector of selectors) {
      const field = this.#querySelectorDeep(dialog, selector);
      if (field) return field;
    }

    return null;
  }

  /**
   * Finds the source field in the form
   * @private
   * @returns {HTMLInputElement | null}
   */
  #findSourceField() {
    const { dialog } = this.refs;

    // Try multiple selectors to find the source field
    const selectors = [
      'input[name*="source" i]',
      'input[id*="source" i]',
      '[data-field-name="source"] input',
      'input[type="hidden"][name*="source" i]',
      'input[type="text"][name*="source" i]',
    ];

    for (const selector of selectors) {
      const field = this.#querySelectorDeep(dialog, selector);
      if (field) return field;
    }

    return null;
  }

  /**
   * Hides the Source field and its label in the shadow DOM
   * @private
   */
  #hideSourceField() {
    const { dialog } = this.refs;

    // Search for the Source field in shadow DOM
    const shadowRoot = this.#querySelectorDeep(dialog, 'shopify-forms-embed')?.shadowRoot;
    if (!shadowRoot) {
      return;
    }

    // Try to find the Source field container in shadow DOM
    const selectors = [
      '[data-field-name*="source" i]',
      '[data-field-type*="source" i]',
      'input[name*="source" i]',
      'input[id*="source" i]',
    ];

    for (const selector of selectors) {
      const elements = shadowRoot.querySelectorAll(selector);
      elements.forEach(element => {
        // Hide the element itself
        element.style.display = 'none';
        element.style.visibility = 'hidden';
        element.style.position = 'absolute';
        element.style.left = '-9999px';

        // Try to hide the parent container (usually wraps label + input)
        let parent = element.parentElement;
        let depth = 0;
        while (parent && depth < 5) {
          // Look for common container class patterns
          const className = parent.className?.toString() || '';
          if (className.includes('field') ||
              className.includes('input') ||
              className.includes('container') ||
              className.includes('wrapper')) {
            parent.style.display = 'none';
            break;
          }
          parent = parent.parentElement;
          depth++;
        }
      });
    }

    // Also search for labels containing "source"
    const allLabels = shadowRoot.querySelectorAll('label');
    allLabels.forEach(label => {
      const text = (label.textContent || '').toLowerCase();
      const forAttr = (label.getAttribute('for') || '').toLowerCase();

      if (text.includes('source') || forAttr.includes('source')) {
        label.style.display = 'none';
      }
    });
  }

  /**
   * Injects custom styles into the Shopify Forms Shadow DOM
   * Uses Constructable Stylesheets API for better performance
   */
  #injectShadowStyles() {
    const { dialog } = this.refs;
    
    const shadowRoot = this.#querySelectorDeep(dialog, 'shopify-forms-embed')?.shadowRoot;
    
    if (!shadowRoot) {
      return;
    }

    // Check if already injected
    const existingSheets = shadowRoot.adoptedStyleSheets || [];
    const alreadyInjected = existingSheets.some(sheet => sheet._lindyCustom === true);
    
    if (alreadyInjected) {
      return;
    }

    // Create stylesheet using Constructable Stylesheets API
    const sheet = new CSSStyleSheet();
    
    // Mark as our custom sheet
    sheet._lindyCustom = true;

    // Replace stylesheet content
    sheet.replaceSync(cssRules);

    // Adopt stylesheet into shadow root
    shadowRoot.adoptedStyleSheets = [...existingSheets, sheet];

    // Setup floating labels for dropdowns
    this.#setupDropdownFloatingLabels(shadowRoot);
  }

  /**
   * Setup floating labels for dropdowns (like input pattern)
   * Uses stable selectors based on aria-controls attribute
   * Only applies to custom dropdowns (starts with "custom#")
   * Automatically excludes phone country selector and system dropdowns
   * @private
   */
  #setupDropdownFloatingLabels(shadowRoot) {
    // Find all custom dropdown buttons (Message Type, Customer Type, etc.)
    // aria-controls starts with "custom#" for custom fields
    const customDropdownButtons = shadowRoot.querySelectorAll('button[type="button"][aria-controls^="custom#"]');
    
    customDropdownButtons.forEach(button => {
      // Find parent container (the div wrapping label + button)
      const container = button.parentElement;
      if (!container) return;

      // Find label (first <p> tag in container)
      const label = container.querySelector('p');
      
      // Find toggle text (first span inside button)
      const toggleText = button.querySelector('span:first-child');
      
      // Validate structure
      if (!label || !toggleText) return;

      // Add marker attribute for CSS targeting
      container.setAttribute('data-lindy-dropdown', '');

      // Check and update has-value attribute
      const updateState = () => {
        const hasValue = toggleText.textContent.trim() !== '';
        if (hasValue) {
          container.setAttribute('data-has-value', '');
        } else {
          container.removeAttribute('data-has-value');
        }
      };

      // Initial check
      updateState();

      // Watch for changes when user selects option
      const observer = new MutationObserver(updateState);
      observer.observe(toggleText, { 
        childList: true, 
        characterData: true, 
        subtree: true 
      });

      // Also update on button click (dropdown interaction)
      button.addEventListener('click', () => {
        setTimeout(updateState, 100);
      });
    });
  }
}

// Register the custom element
if (!customElements.get('lindy-quote-form-dialog')) {
  customElements.define('lindy-quote-form-dialog', LindyQuoteFormDialog);
}

/**
 * Button Handler Component
 * Handles clicks on "Ask a Question" / "Email Me" buttons
 *
 * @extends Component
 */
class LindyQuoteButtonHandler extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleClick);
  }

  /**
   * Handles button click and opens the modal
   * @private
   */
  #handleClick = (event) => {
    event.preventDefault();

    // Find the button element (might be inside the custom element)
    const button = this.querySelector('button') || this;

    // Get product data from data attributes
    const productData = {
      title: button.dataset.productTitle || '',
      sku: button.dataset.productSku || '',
      url: button.dataset.productUrl || '',
      source: button.dataset.source || 'Ask a Question',
    };

    // Get the dialog component
    const dialog = document.getElementById('lindy-quote-form-dialog');

    if (dialog instanceof LindyQuoteFormDialog) {
      dialog.openWithProductData(productData);
    }
  };
}

// Register the button handler custom element
if (!customElements.get('lindy-quote-button')) {
  customElements.define('lindy-quote-button', LindyQuoteButtonHandler);
}

// Also handle clicks from elements that don't use custom elements
// This allows flexibility for different button implementations
document.addEventListener('click', (event) => {
  const button = event.target.closest('.js-open-quote-modal, .js-open-email-modal');

  if (!button) return;

  // Skip if already handled by custom element
  if (button.closest('lindy-quote-button')) {
    return;
  }

  event.preventDefault();

  // Determine source based on button class
  const isEmailMe = button.classList.contains('js-open-email-modal');
  const source = isEmailMe ? 'Email Me' : 'Ask a Question';

  // Get product data from data attributes
  const productData = {
    title: button.dataset.productTitle || '',
    sku: button.dataset.productSku || '',
    url: button.dataset.productUrl || '',
    source: source,
  };

  // Get the dialog component
  const dialog = document.getElementById('lindy-quote-form-dialog');

  if (dialog instanceof LindyQuoteFormDialog) {
    dialog.openWithProductData(productData);
  }
});